import React, { useState, useEffect } from 'react';
import { useSelector, RootStateOrAny, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { OrderTypeNumber } from '../../common/enums/orderTypes.enum';
import { CuoTireTypes, getCuoTireTypeLabel } from '../../common/enums/cuoTireTypes.enum';
import OrderDetailTxt from './forms/edditOrder/OrderDetailTxt';
import OrderDetailLink from './forms/edditOrder/OrderDetailLink';
import { Formik, Form, Field } from 'formik';
import FormDropDown from '../../common/layout/form/FormDropDown';
import NewFormField from '../../common/layout/form/NewFormField';
import NewFormBtns from '../../common/layout/form/NewFormBtns';
import { divider } from '../styles/newOrderForm.styles';
import * as yup from 'yup';
import {
  Panel,
  DetailsList,
  IColumn,
  SelectionMode,
  IDropdownOption,
  MessageBar,
  MessageBarType,
  Spinner,
  SpinnerSize,
  Stack,
  CommandBar,
  Selection,
  Text
} from 'office-ui-fabric-react';
import {
  cuoCompactContainer,
  cuoGridFourColumns,
  cuoCompactSection,
  cuoCompactTitle,
  cuoCompactText,
  cuoCompactClientInfo,
  cuoCompactClientLabel,
  cuoLoadingContainer,
  cuoFormContainer,
  cuoFormTitle,
  cuoFormSection
} from '../styles/detailTxt.style';

import { toggleCuoSplitsPanel } from '../../../config/redux/orders/ordersPanel/orderPanelActions';
import { reloadOrders, setSelectedOrders } from '../../../config/redux/orders/ordersActions';
import { getCuoOriginatedOrders, getCuoOrderDetails, updateCuoOrderSplit } from '../../../connectors/orders/connectors';
import { getOrderTypeItems } from '../../../connectors/orderTypes/connectors';

const cuoSplitValidation = yup.object().shape({
  orderTypeItemId: yup.number().min(1, "wybierz podtyp zamówienia"),
  additionalInfo: yup.string(),
  cuoPositionMappings: yup.array().of(
    yup.object().shape({
      cuoPositionId: yup.number().required(),
      allocatedQuantity: yup.number().min(0, "ilość nie może być ujemna")
    })
  ).test('at-least-one-allocation', 'Przynajmniej jedna pozycja musi mieć ilość większą od 0', function(mappings) {
    return mappings ? mappings.some((mapping: any) => mapping.allocatedQuantity > 0) : false;
  })
});



interface CuoPositionMapping {
  id: number;
  cuoPositionId: number;
  allocatedQuantity: number;
  cuoPosition: {
    id: number;
    cuoPositionId: number;
    name: string;
    cuoTireType: CuoTireTypes | null;
    quantity: number;
  };
}

interface CuoSplitOrder {
  id: number;
  number: string;
  cuoOrderNumber: string;
  orderTypeItemId: number;
  orderTypeItemName: string;
  additionalInfo: string;
  currentStatus: string;
  cuoPositionMappings: CuoPositionMapping[];
  // Computed properties for backward compatibility
  tireQuantity: number; // sum of all allocatedQuantity
  cuoTireType: CuoTireTypes | null; // null if mixed, single type if all same
}



const CuoSplitsPanel: React.FC = () => {
  const dispatch = useDispatch();
  const { cuoSplitsPanelOpen } = useSelector((state: RootStateOrAny) => state.orderPanel);
  const { selectedOrders } = useSelector((state: RootStateOrAny) => state.orders);
  const { t } = useTranslation("orders");
  
  const [orders, setOrders] = useState<CuoSplitOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [orderTypeItems, setOrderTypeItems] = useState<IDropdownOption[]>([]);
  
  const [editForm, setEditForm] = useState({
    orderTypeItemId: 0,
    additionalInfo: '',
    cuoPositionMappings: [] as Array<{
      cuoPositionId: number;
      allocatedQuantity: number;
      cuoPosition: {
        id: number;
        cuoPositionId: number;
        name: string;
        cuoTireType: CuoTireTypes | null;
        quantity: number;
      };
    }>
  });
  const [isEditing, setIsEditing] = useState(false);
  const [editingOrderId, setEditingOrderId] = useState<number | null>(null);


  const [cuoOrderDetails, setCuoOrderDetails] = useState<any>(null);

  const [selection] = useState(() => new Selection({
    onSelectionChanged: () => {
      const selectedItems = selection.getSelection();
      setSelectedSplit(selectedItems.length > 0 ? selectedItems[0] as CuoSplitOrder : null);
    }
  }));
  const [selectedSplit, setSelectedSplit] = useState<CuoSplitOrder | null>(null);

  const cuoOrderId = selectedOrders.length > 0 ? selectedOrders[0].cuoOrderId : null;

  const statusNames = t("orderStatus", { returnObjects: true }) as any;

  const translateStatus = (status: string): string => {
    switch (status) {
      case 'Złożone':
        return statusNames.placed;
      case 'Przyjęte':
        return statusNames.accepted;
      case 'W trakcie realizacji':
        return statusNames.inRealization;
      case 'Zrealizowane':
        return statusNames.realized;
      case 'Anulowane':
        return statusNames.canceled;
      case 'Zafakturowane':
        return statusNames.invoiced;
      default:
        return status;
    }
  };

  useEffect(() => {
    if (cuoSplitsPanelOpen && cuoOrderId) {
      loadCuoOrders();
      loadOrderTypeItems();
      loadCuoOrderDetails();
    }
  }, [cuoSplitsPanelOpen, cuoOrderId]);





  const dismissPanel = () => {
    dispatch(toggleCuoSplitsPanel(false));
    setOrders([]);
    setError(null);
    setSuccess(null);
    setIsEditing(false);
    setEditingOrderId(null);
  };



  const loadCuoOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getCuoOriginatedOrders({ CuoOrderId: cuoOrderId! });
      setOrders(response.data || []);
    } catch (err) {
      console.error('Błąd podczas ładowania zamówień:', err);
      setError('Błąd podczas ładowania zamówień');
    } finally {
      setLoading(false);
    }
  };

  const loadOrderTypeItems = async () => {
    try {
      const response = await getOrderTypeItems(OrderTypeNumber.Vehicle.toString());
      const options = response.data.data.map((item: any) => ({
        key: item.id,
        text: item.name
      }));
      setOrderTypeItems(options);

      if (options.length > 0 && editForm.orderTypeItemId === 0) {
        setEditForm(prev => ({
          ...prev,
          orderTypeItemId: Number(options[0].key)
        }));
      }
    } catch (err) {
      console.error('Błąd podczas ładowania typów zamówień:', err);
    }
  };

  const loadCuoOrderDetails = async () => {
    try {
      const response = await getCuoOrderDetails(cuoOrderId!);
      setCuoOrderDetails(response.data);
    } catch (err) {
      console.error('Błąd podczas ładowania szczegółów zamówienia CUO:', err);
    }
  };

  const columns: IColumn[] = [
    {
      key: 'cuoOrderNumber',
      name: 'Numer CUO',
      fieldName: 'cuoOrderNumber',
      minWidth: 120,
      maxWidth: 150,
      isResizable: true
    },
    {
      key: 'orderTypeItemName',
      name: 'Podtyp',
      fieldName: 'orderTypeItemName',
      minWidth: 150,
      maxWidth: 200,
      isResizable: true
    },
    {
      key: 'cuoTireType',
      name: 'Typ opon',
      fieldName: 'cuoTireType',
      minWidth: 100,
      maxWidth: 120,
      isResizable: true,
      onRender: (item: CuoSplitOrder) => {
        if (item.cuoTireType === null) {
          return "Mieszane";
        }
        return getCuoTireTypeLabel(item.cuoTireType);
      }
    },
    {
      key: 'tireQuantity',
      name: 'Ilość opon',
      fieldName: 'tireQuantity',
      minWidth: 80,
      maxWidth: 100,
      isResizable: true
    },
    {
      key: 'currentStatus',
      name: 'Status',
      fieldName: 'currentStatus',
      minWidth: 120,
      maxWidth: 150,
      isResizable: true,
      onRender: (item: CuoSplitOrder) => translateStatus(item.currentStatus)
    },
    {
      key: 'additionalInfo',
      name: 'Dodatkowe informacje',
      fieldName: 'additionalInfo',
      minWidth: 150,
      isResizable: true,
      onRender: (item: CuoSplitOrder) => (
        <span style={{ display: 'block', whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
          {item.additionalInfo}
        </span>
      )
    }
  ];

  const startEditing = (order?: CuoSplitOrder) => {
    if (order) {
      setEditForm({
        orderTypeItemId: order.orderTypeItemId || (orderTypeItems.length > 0 ? Number(orderTypeItems[0].key) : 0),
        additionalInfo: order.additionalInfo || '',
        cuoPositionMappings: order.cuoPositionMappings || []
      });
      setEditingOrderId(order.id);
    } else {
      // For new orders, initialize mappings based on available CUO positions
      const defaultOrderTypeItemId = orderTypeItems.length > 0 ? Number(orderTypeItems[0].key) : 0;
      const initialMappings = cuoOrderDetails && cuoOrderDetails.positions
        ? cuoOrderDetails.positions.map((position: any) => ({
            cuoPositionId: position.cuoPositionId,
            allocatedQuantity: 0,
            cuoPosition: position
          }))
        : [];

      setEditForm({
        orderTypeItemId: defaultOrderTypeItemId,
        additionalInfo: '',
        cuoPositionMappings: initialMappings
      });
      setEditingOrderId(null);
    }
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditingOrderId(null);
    // Use first available orderTypeItem or 0 as fallback
    const defaultOrderTypeItemId = orderTypeItems.length > 0 ? Number(orderTypeItems[0].key) : 0;
    setEditForm({
      orderTypeItemId: defaultOrderTypeItemId,
      additionalInfo: '',
      cuoPositionMappings: []
    });
  };

  const saveOrder = async (values: any) => {
    if (values.orderTypeItemId === 0) {
      setError('Wybierz typ zamówienia');
      return;
    }

    // Sprawdź czy przynajmniej jedna pozycja ma ilość > 0
    const totalQuantity = values.cuoPositionMappings.reduce((sum: number, mapping: any) => sum + mapping.allocatedQuantity, 0);
    if (totalQuantity === 0) {
      setError('Przynajmniej jedna pozycja musi mieć ilość większą od 0');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const isNewOrder = editingOrderId === null;



      // Konwertuj mapowania na nową strukturę backend (tylko te z ilością > 0)
      const mappingUpdates = values.cuoPositionMappings
        .filter((m: any) => m.allocatedQuantity > 0)
        .map((m: any) => ({
          CuoPositionId: m.cuoPositionId,
          AllocatedQuantity: m.allocatedQuantity
        }));

      const mappingAdds = values.cuoPositionMappings
        .filter((m: any) => m.allocatedQuantity > 0)
        .map((m: any) => ({
          CuoPositionId: m.cuoPositionId,
          AllocatedQuantity: m.allocatedQuantity
        }));

      const updateData = {
        CuoOrderId: cuoOrderId!,
        Updates: isNewOrder ? [] : [{
          SodoOrderId: editingOrderId!,
          OrderTypeItemId: values.orderTypeItemId,
          AdditionalInfo: values.additionalInfo,
          MappingUpdates: mappingUpdates
        }],
        Adds: isNewOrder ? [{
          OrderTypeItemId: values.orderTypeItemId,
          AdditionalInfo: values.additionalInfo,
          MappingAdds: mappingAdds
        }] : [],
        Deletes: []
      };

      await updateCuoOrderSplit(cuoOrderId!, updateData);

      setSuccess(isNewOrder ? 'Zamówienie zostało dodane' : 'Zamówienie zostało zaktualizowane');
      const found = orderTypeItems.find(o => Number(o.key) === values.orderTypeItemId);
      const updatedOrderTypeItemName = found ? String(found.text) : '';
      if (selectedOrders && selectedOrders.length > 0) {
        const updated = {
          ...selectedOrders[0],
          orderTypeItemId: values.orderTypeItemId,
          orderTypeItem: updatedOrderTypeItemName,
        };
        dispatch(setSelectedOrders({ selectedOrders: [updated] }));
      }
      await loadCuoOrderDetails();
      await loadCuoOrders();
    } catch (err) {
      console.error('Błąd podczas zapisywania zamówienia:', err);
      setError('Błąd podczas zapisywania zamówienia');
    } finally {
      setSaving(false);
    }
  };

  const deleteOrder = async (orderId: number) => {
    if (!window.confirm('Czy na pewno chcesz usunąć to zamówienie?')) {
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const updateData = {
        CuoOrderId: cuoOrderId!,
        Updates: [],
        Adds: [],
        Deletes: [orderId]
      };

      await updateCuoOrderSplit(cuoOrderId!, updateData);
      setSuccess('Zamówienie zostało usunięte');
      dispatch(reloadOrders(true));
    } catch (err) {
      console.error('Błąd podczas usuwania zamówienia:', err);
      setError('Błąd podczas usuwania zamówienia');
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = () => {
    startEditing();
  };

  const handleEditSplit = () => {
    if (selectedSplit) {
      startEditing(selectedSplit);
    }
  };



  const handleDelete = () => {
    if (selectedSplit) {
      deleteOrder(selectedSplit.id);
    }
  };

  const commandBarItems = selectedSplit ? [
    {
      key: 'editSplit',
      text: 'Edytuj rozbicie',
      iconProps: { iconName: 'BranchMerge' },
      onClick: handleEditSplit
    },
    {
      key: 'delete',
      text: 'Usuń',
      iconProps: { iconName: 'Delete' },
      onClick: handleDelete
    }
  ] : [
    {
      key: 'add',
      text: 'Dodaj',
      iconProps: { iconName: 'Add' },
      onClick: handleAdd
    }
  ];



  if (!cuoOrderId) {
    return null;
  }



  return (
    <>
      <Panel
        headerText={`Zarządzanie rozbiciami zamówienia CUO ${cuoOrderDetails && cuoOrderDetails.cuoOrderNumber ? cuoOrderDetails.cuoOrderNumber : cuoOrderId}`}
        isOpen={cuoSplitsPanelOpen}
        closeButtonAriaLabel="Close"
        type={4}
        onDismiss={dismissPanel}
        isLightDismiss={false}
        isHiddenOnDismiss={false}
        styles={{
          main: {
            width: '80vw',
            maxWidth: '1200px',
            zIndex: 1000
          },
          content: {
            padding: '20px'
          }
        }}
        isFooterAtBottom={true}
      >
        <Stack tokens={{ childrenGap: 15 }}>
          {error && (
            <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError(null)}>
              {error}
            </MessageBar>
          )}

          {success && (
            <MessageBar messageBarType={MessageBarType.success} onDismiss={() => setSuccess(null)}>
              {success}
            </MessageBar>
          )}

          {cuoOrderDetails && (
            <div className={cuoCompactContainer}>
              {/* Podstawowe informacje */}
              <div className={cuoGridFourColumns}>
                <OrderDetailTxt
                  title="Status CUO"
                  value={cuoOrderDetails.statusName || 'Brak danych'}
                />
                <OrderDetailTxt
                  title="Zamówione opony"
                  value={`${cuoOrderDetails.totalTiresOrdered || 0} szt.`}
                />
                <OrderDetailTxt
                  title="Zrealizowane opony"
                  value={`${cuoOrderDetails.totalTiresRealized || 0} szt.`}
                />
                {cuoOrderDetails.cuoOrderId && (
                  <OrderDetailLink
                    title="Link do CUO"
                    url={`https://app.utylizacjaopon.pl/Order/View/${cuoOrderDetails.cuoOrderId}`}
                  />
                )}
              </div>

        
              {cuoOrderDetails.positions && cuoOrderDetails.positions.length > 0 && (
                <div className={cuoCompactSection}>
                  <div className={cuoCompactTitle}>Pozycje:</div>
                  {cuoOrderDetails.positions.map((position: any, index: number) => {
                    // For Special tire types (including pneumatyczne), show only ordered quantity
                    const isSpecial = position.name && (
                      position.name.toLowerCase().includes('specjalne') ||
                      position.name.toLowerCase().includes('pneumatyczne')
                    );
                    const displayText = isSpecial
                      ? `${position.quantity} szt.`
                      : `${position.quantity} szt. (zrealizowane: ${position.quantityRealized})`;

                    return (
                      <div key={index} className={cuoCompactText}>
                        <strong>{position.name}:</strong> {displayText}
                      </div>
                    );
                  })}
                </div>
              )}
       
              {(cuoOrderDetails.clientName || cuoOrderDetails.firmName || cuoOrderDetails.address) && (
                <div className={cuoCompactClientInfo}>
                  <span className={cuoCompactClientLabel}>Klient: </span>
                  {cuoOrderDetails.clientName && (
                    <span>{cuoOrderDetails.clientName}</span>
                  )}
                  {cuoOrderDetails.clientName && cuoOrderDetails.firmName && <span> | </span>}
                  {cuoOrderDetails.firmName && (
                    <span>Zlecający: {cuoOrderDetails.firmName}</span>
                  )}
                  {(cuoOrderDetails.clientName || cuoOrderDetails.firmName) && cuoOrderDetails.address && <span> | </span>}
                  {cuoOrderDetails.address && (
                    <span>{cuoOrderDetails.address}</span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Command Bar */}
          <CommandBar items={commandBarItems} />



          {loading ? (
            <div className={cuoLoadingContainer}>
              <Spinner size={SpinnerSize.large} label="Ładowanie zamówień..." />
            </div>
          ) : (
            <DetailsList
              items={orders}
              columns={columns}
              selection={selection}
              selectionMode={SelectionMode.single}
              selectionPreservedOnEmptyClick={true}
              setKey="set"
              layoutMode={1}
              isHeaderVisible={true}
            />
          )}

          {isEditing && (
            <div className={cuoFormContainer}>
              <Text styles={cuoFormTitle}>
                {editingOrderId ? 'Edytuj zamówienie' : 'Dodaj nowe zamówienie'}
              </Text>

              <Formik
                initialValues={{
                  orderTypeItemId: editForm.orderTypeItemId,
                  additionalInfo: editForm.additionalInfo,
                  cuoPositionMappings: editForm.cuoPositionMappings
                }}
                validationSchema={cuoSplitValidation}
                onSubmit={async (values) => {
                  await saveOrder(values);
                }}
                enableReinitialize={true}
              >
                {(formikProps) => {
                  return (
                  <Form>
                    <div className={divider}>
                      <FormDropDown
                        name="orderTypeItemId"
                        headerText="Podtyp zamówienia"
                        options={orderTypeItems}
                        placeholder={orderTypeItems.length === 0 ? "Ładowanie typów zamówień..." : "Wybierz typ zamówienia"}
                      />
                    </div>

                    {/* Pozycje CUO do mapowania */}
                    {cuoOrderDetails && cuoOrderDetails.positions && cuoOrderDetails.positions.length > 0 && (
                      <div className={divider}>
                        <Text variant="mediumPlus" style={{ marginBottom: '10px', fontWeight: 600 }}>
                          Przydziel ilości opon z pozycji CUO:
                        </Text>
                        {cuoOrderDetails.positions.map((position: any) => {
                          const existingMapping = formikProps.values.cuoPositionMappings.find(
                            (m: any) => m.cuoPositionId === position.cuoPositionId
                          );
                          const currentValue = existingMapping ? existingMapping.allocatedQuantity : 0;

                          return (
                            <div key={position.cuoPositionId} style={{ marginBottom: '15px', padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}>
                              <Text variant="medium" style={{ marginBottom: '5px', fontWeight: 500 }}>
                                {position.name} (dostępne: {position.quantityAvailable !== undefined ? position.quantityAvailable : position.quantity} szt.)
                              </Text>
                              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 500 }}>
                                Ilość do przydzielenia:
                              </label>
                              <input
                                type="number"
                                min="0"
                                value={currentValue}
                                onChange={(e: any) => {
                                  const newValue = parseInt(e.target.value) || 0;
                                  const updatedMappings = [...formikProps.values.cuoPositionMappings];
                                  const existingIndex = updatedMappings.findIndex(m => m.cuoPositionId === position.cuoPositionId);

                                  if (existingIndex >= 0) {
                                    updatedMappings[existingIndex].allocatedQuantity = newValue;
                                  } else {
                                    updatedMappings.push({
                                      cuoPositionId: position.cuoPositionId,
                                      allocatedQuantity: newValue,
                                      cuoPosition: position
                                    });
                                  }

                                  formikProps.setFieldValue('cuoPositionMappings', updatedMappings);
                                }}
                                style={{
                                  width: '100%',
                                  padding: '8px',
                                  border: '1px solid #ccc',
                                  borderRadius: '4px',
                                  fontSize: '14px'
                                }}
                              />
                            </div>
                          );
                        })}
                      </div>
                    )}

                    <div className={divider}>
                      <NewFormField
                        name="additionalInfo"
                        label="Dodatkowe informacje"
                        multiline={true}
                        required={false}
                      />
                    </div>

                    <div className={cuoFormSection}>
                      <NewFormBtns
                        cancelForm={cancelEditing}
                        submitBtnText={saving ? 'Zapisywanie...' : 'Zapisz'}
                        disabled={saving}
                      />
                    </div>
                  </Form>
                  );
                }}
              </Formik>
            </div>
          )}
        </Stack>
      </Panel>
    </>
  );
};

export default CuoSplitsPanel;
